import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Construction as ConstructionIcon,
  AttachMoney as CostsIcon,
  People as LabourIcon,
  EventNote as LogIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatScheduledDate } from '../../utils/dateUtils';

const ConstructionJobDetails = ({ job, onUpdate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // TKT-193 / TKT-240: gate office-only labour controls on the user's
  // role. Accept admin and superadmin from the legacy taxonomy; sales
  // gets included now in anticipation of the TKT-221 5-tier role model
  // (Sales tier is meant to manage construction projects). Falls back
  // gracefully if `user` hasn't loaded yet - we keep the button hidden
  // rather than flashing the office controls during the AuthContext
  // bootstrap.
  const officeRoles = new Set(['admin', 'superadmin', 'sales']);
  const isAdmin = user != null && officeRoles.has(user.user_type);

  const [expandedSections, setExpandedSections] = useState({
    details: true,
    materials: true,
    labour: true,
    logs: true
  });

  // Project details state
  const [details, setDetails] = useState({
    project_phase: '',
    equipment_required: '',
    contractor_name: '',
    permit_number: '',
    site_conditions: '',
    estimated_duration_days: '',
    actual_duration_days: '',
    ...job.construction_details
  });

  // Costs state
  const [materialCosts, setMaterialCosts] = useState(job.material_costs || []);
  const [labourCosts, setLabourCosts] = useState(job.labour_costs || []);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [costSummary, setCostSummary] = useState(job.cost_summary || { material_total: 0, labour_total: 0, grand_total: 0 });

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [addMaterialOpen, setAddMaterialOpen] = useState(false);
  const [addLabourOpen, setAddLabourOpen] = useState(false);
  const [addLogOpen, setAddLogOpen] = useState(false);
  const [editingCostId, setEditingCostId] = useState(null);
  const [editingLabourId, setEditingLabourId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // New material form state
  const [newMaterial, setNewMaterial] = useState({
    description: '', quantity: 1, unit: '', unit_price: 0
  });

  // New labour form state
  const [newLabour, setNewLabour] = useState({
    user_id: '', total_hours: 0, hourly_rate: 0, notes: '', cost_date: new Date().toISOString().split('T')[0]
  });

  // New log form state
  const [newLog, setNewLog] = useState({
    log_date: new Date().toISOString().split('T')[0],
    work_performed: '', equipment_used: '', materials_used: '',
    weather_conditions: '', site_conditions: '', safety_incidents: '',
    delays: '', notes: ''
  });

  useEffect(() => {
    fetchDailyLogs();
  }, [job.id]);

  const fetchDailyLogs = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-logs`);
      if (response.ok) {
        const data = await response.json();
        setDailyLogs(data);
      }
    } catch (error) {
      console.error('Error fetching construction logs:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (saveFunction) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(saveFunction, 1000);
      };
    })(),
    []
  );

  const showMessage = (msg) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 2000);
  };

  // Surface backend error text to the user. Pre-fix, every save handler
  // silently swallowed non-ok responses and the user assumed their data
  // had saved. Call this in every `else` branch and `catch` to make
  // failures obvious.
  const showError = async (response, fallbackKey = 'constructionLogs.saveFailedGeneric', err) => {
    let msg = fallbackKey ? t(fallbackKey) : 'Save failed';
    if (response) {
      const data = await response.json().catch(() => ({}));
      if (data.error) msg = data.error;
    } else if (err?.message) {
      msg = err.message;
    }
    console.error('Construction save failed:', msg);
    setErrorMessage(msg);
  };

  // ==================
  // Project Details
  // ==================

  const saveDetails = async (updatedDetails) => {
    try {
      setSaving(true);
      const response = await makeApiCall(`api/jobs/${job.id}/construction-details`, {
        method: 'PUT',
        body: JSON.stringify(updatedDetails)
      });
      if (response.ok) {
        showMessage(t('constructionLogs.detailsSaved'));
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDetailChange = (field) => (event) => {
    const updated = { ...details, [field]: event.target.value };
    setDetails(updated);
    debouncedSave(() => saveDetails(updated));
  };

  // ==================
  // Material Costs
  // ==================

  const handleAddMaterial = async () => {
    if (!newMaterial.description) return;
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-costs`, {
        method: 'POST',
        body: JSON.stringify(newMaterial)
      });
      if (response.ok) {
        const added = await response.json();
        setMaterialCosts(prev => [...prev, added]);
        setNewMaterial({ description: '', quantity: 1, unit: '', unit_price: 0 });
        setAddMaterialOpen(false);
        showMessage(t('constructionLogs.materialAdded'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const handleUpdateMaterial = async (costId, updates) => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-costs/${costId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const updated = await response.json();
        setMaterialCosts(prev => prev.map(c => c.id === costId ? updated : c));
        setEditingCostId(null);
        showMessage(t('constructionLogs.materialUpdated'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const handleDeleteMaterial = async (costId) => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-costs/${costId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setMaterialCosts(prev => prev.filter(c => c.id !== costId));
        showMessage(t('constructionLogs.materialDeleted'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  // ==================
  // Labour Costs
  // ==================

  const handleAddLabour = async () => {
    if (!newLabour.user_id) return;
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-labour`, {
        method: 'POST',
        body: JSON.stringify(newLabour)
      });
      if (response.ok) {
        const added = await response.json();
        setLabourCosts(prev => [...prev, added]);
        setNewLabour({ user_id: '', total_hours: 0, hourly_rate: 0, notes: '', cost_date: new Date().toISOString().split('T')[0] });
        setAddLabourOpen(false);
        showMessage(t('constructionLogs.labourAdded'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const handleUpdateLabour = async (labourId, updates) => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-labour/${labourId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const updated = await response.json();
        setLabourCosts(prev => prev.map(l => l.id === labourId ? updated : l));
        setEditingLabourId(null);
        showMessage(t('constructionLogs.labourUpdated'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const handleDeleteLabour = async (labourId) => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-labour/${labourId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setLabourCosts(prev => prev.filter(l => l.id !== labourId));
        showMessage(t('constructionLogs.labourDeleted'));
        refreshCostSummary();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  // ==================
  // Daily Logs
  // ==================

  const handleAddLog = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-logs`, {
        method: 'POST',
        body: JSON.stringify(newLog)
      });
      if (response.ok) {
        setNewLog({
          log_date: new Date().toISOString().split('T')[0],
          work_performed: '', equipment_used: '', materials_used: '',
          weather_conditions: '', site_conditions: '', safety_incidents: '',
          delays: '', notes: ''
        });
        setAddLogOpen(false);
        showMessage(t('constructionLogs.logAdded'));
        fetchDailyLogs();
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-logs/${logId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setDailyLogs(prev => prev.filter(l => l.id !== logId));
        showMessage(t('constructionLogs.logDeleted'));
      } else {
        await showError(response);
      }
    } catch (error) {
      await showError(null, 'constructionLogs.saveFailedGeneric', error);
    }
  };

  const refreshCostSummary = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${job.id}/construction-details`);
      if (response.ok) {
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error refreshing cost summary:', error);
    }
  };

  const confirmDelete = (type, id) => {
    setDeleteTarget({ type, id });
    setDeleteConfirmOpen(true);
  };

  const executeDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'material') handleDeleteMaterial(deleteTarget.id);
    else if (deleteTarget.type === 'labour') handleDeleteLabour(deleteTarget.id);
    else if (deleteTarget.type === 'log') handleDeleteLog(deleteTarget.id);
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return `$${num.toFixed(2)}`;
  };

  const crewMembers = job.assigned_employees || [];

  // Inline editing state for material rows
  const [editMaterialValues, setEditMaterialValues] = useState({});
  const [editLabourValues, setEditLabourValues] = useState({});

  const startEditMaterial = (cost) => {
    setEditingCostId(cost.id);
    setEditMaterialValues({
      description: cost.description,
      quantity: cost.quantity,
      unit: cost.unit || '',
      unit_price: cost.unit_price
    });
  };

  const startEditLabour = (labour) => {
    setEditingLabourId(labour.id);
    setEditLabourValues({
      total_hours: labour.total_hours,
      hourly_rate: labour.hourly_rate,
      notes: labour.notes || ''
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Snackbar
        open={!!saveMessage}
        message={saveMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2000}
      />

      {/* Save error indicator - sticky until dismissed */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErrorMessage('')}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorMessage('')}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Cost Summary Banner */}
      <Card sx={{ mb: 2, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center">
              <ConstructionIcon sx={{ mr: 1 }} />
              <Typography variant="h6">{t('constructionLogs.costSummary')}</Typography>
            </Box>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('constructionLogs.materialCosts')}</Typography>
                <Typography variant="h6">{formatCurrency(costSummary.material_total)}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('constructionLogs.labourCosts')}</Typography>
                <Typography variant="h6">{formatCurrency(costSummary.labour_total)}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('constructionLogs.grandTotal')}</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(costSummary.grand_total)}</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Project Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <ConstructionIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">{t('constructionLogs.projectDetails')}</Typography>
            </Box>
            <IconButton 
              onClick={() => toggleSection('details')} 
              size="small"
              aria-label={expandedSections.details ? t('common.collapse') : t('common.expand')}
            >
              <ExpandIcon sx={{ transform: expandedSections.details ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
            </IconButton>
          </Box>

          <Collapse in={expandedSections.details}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.projectPhase')}
                  value={details.project_phase || ''}
                  onChange={handleDetailChange('project_phase')}
                  aria-label={t('constructionLogs.projectPhase')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.contractorName')}
                  value={details.contractor_name || ''}
                  onChange={handleDetailChange('contractor_name')}
                  aria-label={t('constructionLogs.contractorName')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.permitNumber')}
                  value={details.permit_number || ''}
                  onChange={handleDetailChange('permit_number')}
                  aria-label={t('constructionLogs.permitNumber')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.equipmentRequired')}
                  value={details.equipment_required || ''}
                  onChange={handleDetailChange('equipment_required')}
                  multiline
                  rows={2}
                  aria-label={t('constructionLogs.equipmentRequired')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.siteConditions')}
                  value={details.site_conditions || ''}
                  onChange={handleDetailChange('site_conditions')}
                  multiline
                  rows={2}
                  aria-label={t('constructionLogs.siteConditions')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.estimatedDuration')}
                  value={details.estimated_duration_days || ''}
                  onChange={handleDetailChange('estimated_duration_days')}
                  type="number"
                  aria-label={t('constructionLogs.estimatedDuration')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('constructionLogs.actualDuration')}
                  value={details.actual_duration_days || ''}
                  onChange={handleDetailChange('actual_duration_days')}
                  type="number"
                  aria-label={t('constructionLogs.actualDuration')}
                />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Material Costs */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <CostsIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">{t('constructionLogs.materialCosts')}</Typography>
              <Chip label={materialCosts.length} size="small" sx={{ ml: 1 }} />
            </Box>
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddMaterialOpen(true)}
                aria-label={t('constructionLogs.addMaterial')}
              >
                {t('constructionLogs.addMaterial')}
              </Button>
              <IconButton 
                onClick={() => toggleSection('materials')} 
                size="small"
                aria-label={expandedSections.materials ? t('common.collapse') : t('common.expand')}
              >
                <ExpandIcon sx={{ transform: expandedSections.materials ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
              </IconButton>
            </Box>
          </Box>

          <Collapse in={expandedSections.materials}>
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('constructionLogs.description')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('constructionLogs.quantity')}</strong></TableCell>
                    <TableCell><strong>{t('constructionLogs.unit')}</strong></TableCell>
                    {isAdmin && <TableCell align="right"><strong>{t('constructionLogs.unitPrice')}</strong></TableCell>}
                    {isAdmin && <TableCell align="right"><strong>{t('constructionLogs.totalCost')}</strong></TableCell>}
                    <TableCell align="center" width={100}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materialCosts.map((cost) => (
                    <TableRow key={cost.id}>
                      {editingCostId === cost.id ? (
                        <>
                          <TableCell>
                            <TextField
                              size="small" fullWidth
                              value={editMaterialValues.description}
                              onChange={(e) => setEditMaterialValues(v => ({ ...v, description: e.target.value }))}
                              aria-label={t('constructionLogs.description')}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small" type="number" sx={{ width: 80 }}
                              value={editMaterialValues.quantity}
                              onChange={(e) => setEditMaterialValues(v => ({ ...v, quantity: e.target.value }))}
                              aria-label={t('constructionLogs.quantity')}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small" sx={{ width: 80 }}
                              value={editMaterialValues.unit}
                              onChange={(e) => setEditMaterialValues(v => ({ ...v, unit: e.target.value }))}
                              aria-label={t('constructionLogs.unit')}
                            />
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <TextField
                                size="small" type="number" sx={{ width: 100 }}
                                value={editMaterialValues.unit_price}
                                onChange={(e) => setEditMaterialValues(v => ({ ...v, unit_price: e.target.value }))}
                                aria-label={t('constructionLogs.unitPrice')}
                              />
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell align="right">
                              {formatCurrency((editMaterialValues.quantity || 0) * (editMaterialValues.unit_price || 0))}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleUpdateMaterial(cost.id, editMaterialValues)}
                              aria-label={t('common.save')}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{cost.description}</TableCell>
                          <TableCell align="right">{cost.quantity}</TableCell>
                          <TableCell>{cost.unit || '-'}</TableCell>
                          {isAdmin && <TableCell align="right">{formatCurrency(cost.unit_price)}</TableCell>}
                          {isAdmin && <TableCell align="right">{formatCurrency(cost.total_cost)}</TableCell>}
                          <TableCell align="center">
                            <Tooltip title={t('common.edit')}>
                              <IconButton size="small" onClick={() => startEditMaterial(cost)} aria-label={t('common.edit')}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                              <IconButton size="small" color="error" onClick={() => confirmDelete('material', cost.id)} aria-label={t('common.delete')}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {materialCosts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        {t('constructionLogs.noMaterials')}
                      </TableCell>
                    </TableRow>
                  )}
                  {isAdmin && materialCosts.length > 0 && (
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={3}><strong>{t('constructionLogs.subtotal')}</strong></TableCell>
                      <TableCell align="right"></TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(materialCosts.reduce((sum, c) => sum + parseFloat(c.total_cost || 0), 0))}</strong>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </CardContent>
      </Card>

      {/* Labour Costs */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <LabourIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">{t('constructionLogs.labourCosts')}</Typography>
              <Chip label={labourCosts.length} size="small" sx={{ ml: 1 }} />
            </Box>
            <Box>
              {isAdmin && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddLabourOpen(true)}
                  aria-label={t('constructionLogs.addLabour')}
                >
                  {t('constructionLogs.addLabour')}
                </Button>
              )}
              <IconButton 
                onClick={() => toggleSection('labour')} 
                size="small"
                aria-label={expandedSections.labour ? t('common.collapse') : t('common.expand')}
              >
                <ExpandIcon sx={{ transform: expandedSections.labour ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
              </IconButton>
            </Box>
          </Box>

          <Collapse in={expandedSections.labour}>
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('constructionLogs.employee')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('constructionLogs.totalHours')}</strong></TableCell>
                    {isAdmin && <TableCell align="right"><strong>{t('constructionLogs.hourlyRate')}</strong></TableCell>}
                    {isAdmin && <TableCell align="right"><strong>{t('constructionLogs.totalCost')}</strong></TableCell>}
                    <TableCell><strong>{t('constructionLogs.notes')}</strong></TableCell>
                    <TableCell><strong>{t('constructionLogs.date')}</strong></TableCell>
                    {isAdmin && <TableCell align="center" width={100}></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labourCosts.map((labour) => (
                    <TableRow key={labour.id}>
                      {editingLabourId === labour.id ? (
                        <>
                          <TableCell>{labour.first_name} {labour.last_name}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small" type="number" sx={{ width: 80 }}
                              value={editLabourValues.total_hours}
                              onChange={(e) => setEditLabourValues(v => ({ ...v, total_hours: e.target.value }))}
                              aria-label={t('constructionLogs.totalHours')}
                            />
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <TextField
                                size="small" type="number" sx={{ width: 100 }}
                                value={editLabourValues.hourly_rate}
                                onChange={(e) => setEditLabourValues(v => ({ ...v, hourly_rate: e.target.value }))}
                                aria-label={t('constructionLogs.hourlyRate')}
                              />
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell align="right">
                              {formatCurrency((editLabourValues.total_hours || 0) * (editLabourValues.hourly_rate || 0))}
                            </TableCell>
                          )}
                          <TableCell>
                            <TextField
                              size="small" fullWidth
                              value={editLabourValues.notes}
                              onChange={(e) => setEditLabourValues(v => ({ ...v, notes: e.target.value }))}
                              aria-label={t('constructionLogs.notes')}
                            />
                          </TableCell>
                          <TableCell>{formatScheduledDate(labour.cost_date) || '-'}</TableCell>
                          {isAdmin && (
                            <TableCell align="center">
                              <IconButton size="small" color="primary" onClick={() => handleUpdateLabour(labour.id, editLabourValues)} aria-label={t('common.save')}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </>
                      ) : (
                        <>
                          <TableCell>{labour.first_name} {labour.last_name}</TableCell>
                          <TableCell align="right">{labour.total_hours}h</TableCell>
                          {isAdmin && <TableCell align="right">{formatCurrency(labour.hourly_rate)}/h</TableCell>}
                          {isAdmin && <TableCell align="right">{formatCurrency(labour.total_cost)}</TableCell>}
                          <TableCell>{labour.notes || '-'}</TableCell>
                          <TableCell>{formatScheduledDate(labour.cost_date) || '-'}</TableCell>
                          {isAdmin && (
                            <TableCell align="center">
                              <Tooltip title={t('common.edit')}>
                                <IconButton size="small" onClick={() => startEditLabour(labour)} aria-label={t('common.edit')}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common.delete')}>
                                <IconButton size="small" color="error" onClick={() => confirmDelete('labour', labour.id)} aria-label={t('common.delete')}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  ))}
                  {labourCosts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        {t('constructionLogs.noLabour')}
                      </TableCell>
                    </TableRow>
                  )}
                  {isAdmin && labourCosts.length > 0 && (
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={3}><strong>{t('constructionLogs.subtotal')}</strong></TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(labourCosts.reduce((sum, l) => sum + parseFloat(l.total_cost || 0), 0))}</strong>
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </CardContent>
      </Card>

      {/* Daily Logs */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <LogIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">{t('constructionLogs.dailyLogs')}</Typography>
              <Chip label={dailyLogs.length} size="small" sx={{ ml: 1 }} />
            </Box>
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddLogOpen(true)}
                aria-label={t('constructionLogs.addEntry')}
              >
                {t('constructionLogs.addEntry')}
              </Button>
              <IconButton 
                onClick={() => toggleSection('logs')} 
                size="small"
                aria-label={expandedSections.logs ? t('common.collapse') : t('common.expand')}
              >
                <ExpandIcon sx={{ transform: expandedSections.logs ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
              </IconButton>
            </Box>
          </Box>

          <Collapse in={expandedSections.logs}>
            <Box sx={{ mt: 2 }}>
              {dailyLogs.length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  {t('constructionLogs.noLogs')}
                </Typography>
              )}
              {dailyLogs.map((log) => (
                <Card key={log.id} variant="outlined" sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={formatScheduledDate(log.log_date)}
                          size="small"
                          color="warning"
                        />
                        {log.created_by_username && (
                          <Typography variant="caption" color="text.secondary">
                            {log.first_name} {log.last_name}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" color="error" onClick={() => confirmDelete('log', log.id)} aria-label={t('common.delete')}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {log.work_performed && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.workPerformed')}:</Typography>
                        <Typography variant="body2">{log.work_performed}</Typography>
                      </Box>
                    )}
                    {log.equipment_used && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.equipmentUsed')}:</Typography>
                        <Typography variant="body2">{log.equipment_used}</Typography>
                      </Box>
                    )}
                    {log.materials_used && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.materialsUsed')}:</Typography>
                        <Typography variant="body2">{log.materials_used}</Typography>
                      </Box>
                    )}
                    {log.weather_conditions && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.weatherConditions')}:</Typography>
                        <Typography variant="body2">{log.weather_conditions}</Typography>
                      </Box>
                    )}
                    {log.safety_incidents && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="error.main">{t('constructionLogs.safetyIncidents')}:</Typography>
                        <Typography variant="body2" color="error.main">{log.safety_incidents}</Typography>
                      </Box>
                    )}
                    {log.delays && (
                      <Box mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.delays')}:</Typography>
                        <Typography variant="body2">{log.delays}</Typography>
                      </Box>
                    )}
                    {log.notes && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('constructionLogs.notes')}:</Typography>
                        <Typography variant="body2">{log.notes}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Add Material Dialog */}
      <Dialog open={addMaterialOpen} onClose={() => setAddMaterialOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('constructionLogs.addMaterial')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('constructionLogs.description')}
                value={newMaterial.description}
                onChange={(e) => setNewMaterial(v => ({ ...v, description: e.target.value }))}
                required
                aria-label={t('constructionLogs.description')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth type="number"
                label={t('constructionLogs.quantity')}
                value={newMaterial.quantity}
                onChange={(e) => setNewMaterial(v => ({ ...v, quantity: e.target.value }))}
                aria-label={t('constructionLogs.quantity')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label={t('constructionLogs.unit')}
                value={newMaterial.unit}
                onChange={(e) => setNewMaterial(v => ({ ...v, unit: e.target.value }))}
                placeholder="ea, bag, m², ..."
                aria-label={t('constructionLogs.unit')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth type="number"
                label={t('constructionLogs.unitPrice')}
                value={newMaterial.unit_price}
                onChange={(e) => setNewMaterial(v => ({ ...v, unit_price: e.target.value }))}
                aria-label={t('constructionLogs.unitPrice')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMaterialOpen(false)} aria-label={t('common.cancel')}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddMaterial} disabled={!newMaterial.description} aria-label={t('common.save')}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Labour Dialog */}
      <Dialog open={addLabourOpen} onClose={() => setAddLabourOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('constructionLogs.addLabour')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth select
                label={t('constructionLogs.employee')}
                value={newLabour.user_id}
                onChange={(e) => setNewLabour(v => ({ ...v, user_id: e.target.value }))}
                SelectProps={{ native: true }}
                aria-label={t('constructionLogs.employee')}
              >
                <option value="">{t('constructionLogs.selectEmployee')}</option>
                {crewMembers.map((emp) => (
                  <option key={emp.user_id} value={emp.user_id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth type="number"
                label={t('constructionLogs.totalHours')}
                value={newLabour.total_hours}
                onChange={(e) => setNewLabour(v => ({ ...v, total_hours: e.target.value }))}
                aria-label={t('constructionLogs.totalHours')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth type="number"
                label={t('constructionLogs.hourlyRate')}
                value={newLabour.hourly_rate}
                onChange={(e) => setNewLabour(v => ({ ...v, hourly_rate: e.target.value }))}
                aria-label={t('constructionLogs.hourlyRate')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth type="date"
                label={t('constructionLogs.date')}
                value={newLabour.cost_date}
                onChange={(e) => setNewLabour(v => ({ ...v, cost_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                aria-label={t('constructionLogs.date')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2}
                label={t('constructionLogs.notes')}
                value={newLabour.notes}
                onChange={(e) => setNewLabour(v => ({ ...v, notes: e.target.value }))}
                aria-label={t('constructionLogs.notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLabourOpen(false)} aria-label={t('common.cancel')}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddLabour} disabled={!newLabour.user_id} aria-label={t('common.save')}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Daily Log Dialog */}
      <Dialog open={addLogOpen} onClose={() => setAddLogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('constructionLogs.addEntry')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date"
                label={t('constructionLogs.date')}
                value={newLog.log_date}
                onChange={(e) => setNewLog(v => ({ ...v, log_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                aria-label={t('constructionLogs.date')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('constructionLogs.weatherConditions')}
                value={newLog.weather_conditions}
                onChange={(e) => setNewLog(v => ({ ...v, weather_conditions: e.target.value }))}
                aria-label={t('constructionLogs.weatherConditions')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={3}
                label={t('constructionLogs.workPerformed')}
                value={newLog.work_performed}
                onChange={(e) => setNewLog(v => ({ ...v, work_performed: e.target.value }))}
                aria-label={t('constructionLogs.workPerformed')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth multiline rows={2}
                label={t('constructionLogs.equipmentUsed')}
                value={newLog.equipment_used}
                onChange={(e) => setNewLog(v => ({ ...v, equipment_used: e.target.value }))}
                aria-label={t('constructionLogs.equipmentUsed')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth multiline rows={2}
                label={t('constructionLogs.materialsUsed')}
                value={newLog.materials_used}
                onChange={(e) => setNewLog(v => ({ ...v, materials_used: e.target.value }))}
                aria-label={t('constructionLogs.materialsUsed')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('constructionLogs.siteConditions')}
                value={newLog.site_conditions}
                onChange={(e) => setNewLog(v => ({ ...v, site_conditions: e.target.value }))}
                aria-label={t('constructionLogs.siteConditions')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('constructionLogs.safetyIncidents')}
                value={newLog.safety_incidents}
                onChange={(e) => setNewLog(v => ({ ...v, safety_incidents: e.target.value }))}
                aria-label={t('constructionLogs.safetyIncidents')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('constructionLogs.delays')}
                value={newLog.delays}
                onChange={(e) => setNewLog(v => ({ ...v, delays: e.target.value }))}
                aria-label={t('constructionLogs.delays')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth multiline rows={2}
                label={t('constructionLogs.notes')}
                value={newLog.notes}
                onChange={(e) => setNewLog(v => ({ ...v, notes: e.target.value }))}
                aria-label={t('constructionLogs.notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLogOpen(false)} aria-label={t('common.cancel')}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddLog} aria-label={t('common.save')}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('constructionLogs.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('constructionLogs.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} aria-label={t('common.cancel')}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={executeDelete} aria-label={t('common.delete')}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConstructionJobDetails;
